/**
 * useLocationManagement - 位置情報・座標管理用のComposable
 * 
 * 施設の位置情報管理、座標補正、地図編集機能を提供します。
 */
import { ref, reactive, computed, nextTick } from 'vue'
import { developerSettingsApi } from '@/services'

export function useLocationManagement() {
    // 位置情報データ
    const locationData = ref([])
    const isEditMode = ref(false)
    const currentEditingLocation = ref(null)
    const pendingLocationChange = ref(null)
    const coordinateOffset = ref({
        latitudeOffset: 0,
        longitudeOffset: 0,
    })

    // ローディング状態
    const loading = reactive({
        facilities: false,
        coordinateCorrection: false,
    })

    // モーダル状態
    const modals = reactive({
        confirmClear: { visible: false, location: null },
        coordinateEdit: { visible: false },
        locationConfirm: { visible: false },
    })

    // 地図表示用のマーカー
    const editMapMarkers = computed(() => {
        if (!currentEditingLocation.value ||
            currentEditingLocation.value.latitude == null ||
            currentEditingLocation.value.longitude == null) {
            return []
        }
        return [{
            id: currentEditingLocation.value.id,
            lat: currentEditingLocation.value.latitude,
            lng: currentEditingLocation.value.longitude,
            title: currentEditingLocation.value.name,
        }]
    })

    // 地図の中心座標
    const editMapCenter = computed(() => {
        if (currentEditingLocation.value &&
            currentEditingLocation.value.latitude != null &&
            currentEditingLocation.value.longitude != null) {
            return [currentEditingLocation.value.longitude, currentEditingLocation.value.latitude]
        }

        const cityHall = locationData.value.find(item => item.name === '市役所')
        if (cityHall && cityHall.latitude != null && cityHall.longitude != null) {
            return [cityHall.longitude, cityHall.latitude]
        }

        return [139.6917, 35.6895] // デフォルトは東京
    })

    // 地図のズームレベル
    const editMapZoom = computed(() => {
        return (currentEditingLocation.value && currentEditingLocation.value.latitude) ? 16 : 12
    })

    /**
     * 座標を小数点以下6桁に丸める
     */
    const roundCoordinate = (coordinate) => {
        if (coordinate == null) return null
        return Math.round(coordinate * 1000000) / 1000000
    }

    /**
     * 施設情報を取得
     */
    const loadFacilities = async () => {
        loading.facilities = true
        try {
            const facilities = await developerSettingsApi.getFacilities()
            console.log('[Container] 施設情報を取得:', facilities)
            return facilities
        } catch (error) {
            console.error('[Container] 施設情報の取得に失敗:', error)
            throw error
        } finally {
            loading.facilities = false
        }
    }

    /**
     * 座標変換補正値を取得
     */
    const loadCoordinateCorrection = async () => {
        loading.coordinateCorrection = true
        try {
            const correction = await developerSettingsApi.getCoordinateCorrection()
            console.log('[Container] 座標変換補正値を取得:', correction)
            return correction
        } catch (error) {
            console.error('[Container] 座標変換補正値の取得に失敗:', error)
            throw error
        } finally {
            loading.coordinateCorrection = false
        }
    }

    /**
     * 位置編集モードを開始
     */
    const handleLocationEdit = (location) => {
        currentEditingLocation.value = { ...location }
        isEditMode.value = true
    }

    /**
     * 位置情報クリアの確認
     */
    const handleLocationClear = (location) => {
        modals.confirmClear.location = location
        modals.confirmClear.visible = true
    }

    /**
     * 位置情報クリアの実行
     */
    const handleClearConfirm = async () => {
        const locationToClear = modals.confirmClear.location
        if (!locationToClear) return null

        try {
            console.log('[Container] 位置情報をクリア:', locationToClear.id)
            const clearedFacility = await developerSettingsApi.clearFacilityLocation(locationToClear.id)

            const index = locationData.value.findIndex(item => item.id === locationToClear.id)
            if (index !== -1) {
                locationData.value.splice(index, 1, clearedFacility)
            }

            if (currentEditingLocation.value && currentEditingLocation.value.id === locationToClear.id) {
                currentEditingLocation.value.latitude = null
                currentEditingLocation.value.longitude = null
            }

            console.log('[Container] 位置情報のクリア完了:', clearedFacility)
            return null // エラーなし
        } catch (error) {
            console.error('[Container] 位置情報のクリアに失敗:', error)
            return error.message
        }
    }

    /**
     * 座標設定画面を開く
     */
    const handleCoordinateSettings = () => {
        modals.coordinateEdit.visible = true
    }

    /**
     * 座標補正値の保存
     */
    const handleCoordinateSave = async (coordinateData) => {
        try {
            const roundedLatOffset = roundCoordinate(coordinateData.latitudeOffset)
            const roundedLngOffset = roundCoordinate(coordinateData.longitudeOffset)

            console.log('[Container] 座標変換補正値を更新:', { roundedLatOffset, roundedLngOffset })

            const updatedCorrection = await developerSettingsApi.updateCoordinateCorrection(
                roundedLatOffset,
                roundedLngOffset
            )

            coordinateOffset.value = updatedCorrection
            console.log('[Container] 座標変換補正値の更新完了:', updatedCorrection)
            return null // エラーなし
        } catch (error) {
            console.error('[Container] 座標変換補正値の更新に失敗:', error)
            return error.message
        }
    }

    /**
     * 編集モードを終了
     */
    const exitEditMode = () => {
        isEditMode.value = false
        currentEditingLocation.value = null
        pendingLocationChange.value = null
    }

    /**
     * 地図クリック時の処理
     */
    const handleMapClick = (coordinate) => {
        if (!currentEditingLocation.value) return

        const roundedLat = roundCoordinate(coordinate.lat)
        const roundedLng = roundCoordinate(coordinate.lng)

        pendingLocationChange.value = {
            facilityName: currentEditingLocation.value.name,
            originalPosition: {
                lat: currentEditingLocation.value.latitude || 0,
                lng: currentEditingLocation.value.longitude || 0
            },
            newPosition: { lat: roundedLat, lng: roundedLng },
        }
        modals.locationConfirm.visible = true
    }

    /**
     * マーカードラッグ終了時の処理
     */
    const handleMarkerDragEnd = (dragData) => {
        if (!currentEditingLocation.value) return

        const roundedLat = roundCoordinate(dragData.newPosition.lat)
        const roundedLng = roundCoordinate(dragData.newPosition.lng)

        pendingLocationChange.value = {
            facilityName: currentEditingLocation.value.name,
            originalPosition: {
                lat: currentEditingLocation.value.latitude || 0,
                lng: currentEditingLocation.value.longitude || 0
            },
            newPosition: { lat: roundedLat, lng: roundedLng },
        }
        modals.locationConfirm.visible = true
    }

    /**
     * 位置確認の実行
     */
    const handleLocationConfirm = async () => {
        if (!pendingLocationChange.value || !currentEditingLocation.value) {
            return null
        }

        try {
            const { lat, lng } = pendingLocationChange.value.newPosition
            const facilityId = currentEditingLocation.value.id

            const roundedLat = roundCoordinate(lat)
            const roundedLng = roundCoordinate(lng)

            console.log('[Container] 位置情報を更新:', { facilityId, roundedLat, roundedLng })

            const updatedFacility = await developerSettingsApi.updateFacilityLocation(
                facilityId,
                roundedLat,
                roundedLng
            )

            currentEditingLocation.value.latitude = updatedFacility.latitude
            currentEditingLocation.value.longitude = updatedFacility.longitude

            const index = locationData.value.findIndex(item => item.id === facilityId)
            if (index !== -1) {
                locationData.value.splice(index, 1, updatedFacility)
            }

            console.log('[Container] 位置情報の更新完了:', updatedFacility)
            pendingLocationChange.value = null
            return null // エラーなし
        } catch (error) {
            console.error('[Container] 位置情報の更新に失敗:', error)
            await handleLocationCancel()
            return error.message
        }
    }

    /**
     * 位置変更のキャンセル
     */
    const handleLocationCancel = async () => {
        if (pendingLocationChange.value && currentEditingLocation.value) {
            const originalLat = pendingLocationChange.value.originalPosition.lat
            const originalLng = pendingLocationChange.value.originalPosition.lng

            const resetLat = (originalLat === 0 && originalLng === 0) ? null : originalLat
            const resetLng = (originalLat === 0 && originalLng === 0) ? null : originalLng

            currentEditingLocation.value.latitude = null
            currentEditingLocation.value.longitude = null

            const index = locationData.value.findIndex(item => item.id === currentEditingLocation.value.id)
            if (index !== -1) {
                locationData.value[index].latitude = null
                locationData.value[index].longitude = null
            }

            await nextTick(() => {
                currentEditingLocation.value.latitude = resetLat
                currentEditingLocation.value.longitude = resetLng

                if (index !== -1) {
                    locationData.value[index].latitude = resetLat
                    locationData.value[index].longitude = resetLng
                }
            })
        }

        pendingLocationChange.value = null
    }

    return {
        // State
        locationData,
        isEditMode,
        currentEditingLocation,
        pendingLocationChange,
        coordinateOffset,
        loading,
        modals,

        // Computed
        editMapMarkers,
        editMapCenter,
        editMapZoom,

        // Methods
        roundCoordinate,
        loadFacilities,
        loadCoordinateCorrection,
        handleLocationEdit,
        handleLocationClear,
        handleClearConfirm,
        handleCoordinateSettings,
        handleCoordinateSave,
        exitEditMode,
        handleMapClick,
        handleMarkerDragEnd,
        handleLocationConfirm,
        handleLocationCancel,
    }
}
